import { useState } from "react";
import { Alert, Button, InputSwitch, SectionCard, TextArea } from "@/deps/ui-library";
import {
  useDocumentApproval,
  useSetYearSoftLock,
  useWithdrawYearClose,
  useYearCloseGates,
} from "@/api/queries";
import { useCan } from "@/app/session";
import { ApprovalCard, isLive } from "@/components/DocumentApproval";
import { Dialog } from "@/components/Dialog";

/**
 * The completion gates and the soft lock (ADR-0059). Approving and closing are on the card below, which
 * follows the company's approval rule for a year-end close.
 */
export function YearApprovalCard({ fiscalYearId }: { fiscalYearId: string }) {
  const can = useCan();
  const { data } = useYearCloseGates(fiscalYearId);
  const { data: approval } = useDocumentApproval("yearClose", data?.documentId ?? "", !!data);
  const softLock = useSetYearSoftLock(fiscalYearId);
  const withdraw = useWithdrawYearClose(fiscalYearId);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");

  if (!data || data.closed) return null;

  const request = approval?.request;
  const live = isLive(request);
  const failure = softLock.error ?? withdraw.error;

  return (
    <>
      <SectionCard title="Before this year can close">
        <div className="flex flex-col gap-3 text-sm">
          {failure ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "danger" }}
              description={(failure as Error).message}
            />
          ) : null}
          <ul className="flex flex-col gap-1.5">
            {data.gates.map((gate) => (
              <li key={gate.name} className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-fg">
                  <span className={gate.passed ? "text-good-text" : "text-bad-text"}>
                    {gate.passed ? "Pass" : "Not yet"}
                  </span>{" "}
                  · {gate.name}
                </span>
                <span className="text-fg-muted text-xs">{gate.detail}</span>
              </li>
            ))}
          </ul>
          {can("accounting.periods.manage") ? (
            <InputSwitch
              label="Soft lock"
              helperText={
                live
                  ? "It stays on while this is with the approvers."
                  : "Nothing posts into this year while it's on. You can turn it off again."
              }
              checked={data.softLocked}
              disabled={live || softLock.isPending}
              onCheckedChange={(details: { checked: boolean }) => softLock.mutate(details.checked)}
            />
          ) : null}
          {live && request?.mine && request.step ? (
            <div>
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                disabled={withdraw.isPending}
                onClick={() => setWithdrawing(true)}
              >
                Withdraw request
              </Button>
            </div>
          ) : null}
        </div>
      </SectionCard>

      <ApprovalCard kind="yearClose" id={data.documentId} />

      <Dialog
        open={withdrawing}
        onClose={() => setWithdrawing(false)}
        title="Withdraw this request?"
        description="It comes back to you, and the soft lock stays on. This goes in the close log."
        footer={
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={withdraw.isPending}
            onClick={() =>
              withdraw.mutate(reason.trim() || undefined, {
                onSettled: () => setWithdrawing(false),
              })
            }
          >
            {withdraw.isPending ? "Withdrawing" : "Withdraw request"}
          </Button>
        }
      >
        <TextArea
          label="Reason"
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Dialog>
    </>
  );
}
